<template>
  <div>
    <section class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6"></div>
        </div>
      </div>
    </section>
    <section class="content">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-12">
            <!-- Purchase Form Card - Hidden for Accountant users (read-only access) -->
            <div class="card card-success" v-if="userDetails.role_name !== 'Accountant'">
              <div class="card-header">
                <h3 class="card-title">Issue Gift Card</h3>
              </div>
              <div class="loading" v-if="loading">
                <hour-glass></hour-glass>
              </div>
              <div class="card-body">
                <form @submit.prevent="purchaseGiftCard">
                  <div class="row">
                    <!-- Store Selection with Search -->
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="storeSelect">Select Store <span class="text-danger">*</span></label>
                        <multiselect
                          id="storeSelect"
                          v-model="selectedStore"
                          :options="authorizedStores"
                          :searchable="true"
                          :show-labels="false"
                          :allow-empty="false"
                          label="retailer"
                          track-by="id"
                          placeholder="Search and select store"
                          @input="onStoreSelect"
                        >
                          <template slot="option" slot-scope="props">
                            <div class="store-option">
                              <strong>{{ props.option.retailer }}</strong>
                              <br>
                              <small>{{ props.option.city }}</small>
                            </div>
                          </template>
                          <template slot="singleLabel" slot-scope="props">
                            <span>{{ props.option.retailer }} - {{ props.option.city }}</span>
                          </template>
                        </multiselect>
                        <span v-if="giftcardFromErrors.store_id" class="text-danger small d-block mt-1">
                          {{ giftcardFromErrors.store_id }}
                        </span>
                      </div>
                    </div>

                    <!-- Delivery Type Selection -->
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>How Will Gift Card Be Delivered: <span class="text-danger">*</span></label>
                        <div class="d-flex align-items-center" style="gap: 20px; margin-top: 8px;">
                          <div class="custom-control custom-radio">
                            <input
                              type="radio"
                              id="deliveryTypeDigital"
                              name="deliveryType"
                              class="custom-control-input"
                              value="digital"
                              v-model="purchaseForm.delivery_type"
                              @change="onDeliveryTypeChange"
                            />
                            <label class="custom-control-label" for="deliveryTypeDigital">
                              <i class="fas fa-envelope mr-1"></i> Email
                            </label>
                          </div>
                          <div class="custom-control custom-radio">
                            <input
                              type="radio"
                              id="deliveryTypePrintable"
                              name="deliveryType"
                              class="custom-control-input"
                              value="printable"
                              v-model="purchaseForm.delivery_type"
                              @change="onDeliveryTypeChange"
                            />
                            <label class="custom-control-label" for="deliveryTypePrintable">
                              <i class="fas fa-ticket-alt mr-1"></i> Print Code for in-person Gift
                            </label>
                            <i class="fas fa-info-circle ml-1 text-info"
                               style="cursor: pointer;"
                               @click="showPrintCodeInfo"
                               title="Click for more information"></i>
                          </div>
                        </div>
                        <span v-if="giftcardFromErrors.delivery_type" class="text-danger small d-block mt-1">
                          {{ giftcardFromErrors.delivery_type }}
                        </span>
                        <small class="form-text text-muted" v-if="purchaseForm.delivery_type === 'digital'">
                          Gift Card notification will be sent via Email to the recipient. Recipients with an existing CanPay account will also receive notification of the gift card in the CanPay App.
                        </small>
                        <small class="form-text text-muted" v-if="purchaseForm.delivery_type === 'printable'">
                          Note: A unique Gift Card Code will be generated after pressing Confirm & Issue. Write this code down for the Sender, and provide them with the website: www.canpaygift.com for the Gift Card Recipient to access the gift card funds.
                        </small>
                      </div>
                    </div>

                    <!-- Sender's Name with Real-time Validation -->
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="senderName">
                          Sender's Name
                          <span class="text-danger" v-if="purchaseForm.delivery_type === 'digital'">*</span>
                        </label>
                        <input
                          type="text"
                          id="senderName"
                          class="form-control"
                          v-model="purchaseForm.sender_name"
                          :class="{ 'is-invalid': giftcardFromErrors.sender_name }"
                          :placeholder="purchaseForm.delivery_type === 'printable' ? 'e.g., John\'s Restaurant (optional)' : 'Enter your name (recipient will recognize it)'"
                          @input="validateSenderNameOnInput"
                          @blur="validateSenderName"
                          maxlength="100"
                        />
                        <span v-if="giftcardFromErrors.sender_name" class="text-danger small d-block mt-1">
                          {{ giftcardFromErrors.sender_name }}
                        </span>
                      </div>
                    </div>

                    <!-- Gift Card Value with Professional Validation -->
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="amount">Gift Card Value <span class="text-danger">*</span></label>
                        <div class="input-group">
                          <div class="input-group-prepend">
                            <span class="input-group-text">$</span>
                          </div>
                          <input
                            type="text"
                            id="amount"
                            class="form-control"
                            v-model="purchaseForm.amount"
                            :class="{ 'is-invalid': giftcardFromErrors.amount }"
                            placeholder="0.00"
                            @input="validateAmountOnInput"
                            @blur="validateAmount"
                            maxlength="10"
                          />
                        </div>
                        <span v-if="giftcardFromErrors.amount" class="text-danger small d-block mt-1">
                          {{ giftcardFromErrors.amount }}
                        </span>
                        <!-- <small class="form-text text-muted">Enter amount between $0.00 and $1000.00</small> -->
                      </div>
                    </div>

                    <!-- Recipient Fields - Only show for Digital delivery type -->
                    <template v-if="purchaseForm.delivery_type === 'digital'">
                      <!-- Recipient's Name with Real-time Validation -->
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="consumerName">Recipient's Name <span class="text-danger">*</span></label>
                          <input
                            type="text"
                            id="consumerName"
                            class="form-control"
                            v-model="purchaseForm.recipient_name"
                            :class="{ 'is-invalid': giftcardFromErrors.recipient_name }"
                            placeholder="Enter consumer name"
                            @input="validateRecipientNameOnInput"
                            @blur="validateRecipientName"
                            maxlength="100"
                          />
                          <span v-if="giftcardFromErrors.recipient_name" class="text-danger small d-block mt-1">
                            {{ giftcardFromErrors.recipient_name }}
                          </span>
                        </div>
                      </div>

                      <!-- Recipient's Phone with Auto-formatting -->
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="consumerPhone">Recipient's Phone <span class="text-danger">*</span></label>
                          <input
                            type="tel"
                            id="consumerPhone"
                            class="form-control"
                            v-model="purchaseForm.recipient_phone"
                            :class="{ 'is-invalid': giftcardFromErrors.recipient_phone }"
                            placeholder="(555) 123-4567"
                            @input="formatPhoneNumber"
                            @blur="validatePhone"
                            maxlength="14"
                          />
                          <span v-if="giftcardFromErrors.recipient_phone" class="text-danger small d-block mt-1">
                            {{ giftcardFromErrors.recipient_phone }}
                          </span>
                          <small class="form-text text-muted">Format: (555) 123-4567</small>
                        </div>
                      </div>

                      <!-- Recipient's Email with Validation -->
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="consumerEmail">Recipient's Email <span class="text-danger">*</span></label>
                          <input
                            type="email"
                            id="consumerEmail"
                            class="form-control"
                            v-model="purchaseForm.recipient_email"
                            :class="{ 'is-invalid': giftcardFromErrors.recipient_email }"
                            placeholder="consumer@example.com"
                            @input="validateEmailOnInput"
                            @blur="validateEmail"
                          />
                          <span v-if="giftcardFromErrors.recipient_email" class="text-danger small d-block mt-1">
                            {{ giftcardFromErrors.recipient_email }}
                          </span>
                        </div>
                      </div>

                      <!-- Delivery Date Options -->
                      <div class="col-md-6">
                        <div class="form-group">
                          <label>Delivery Date <span class="text-danger">*</span></label>
                          <div class="d-flex align-items-center" style="gap: 20px; margin-top: 8px;">
                            <div class="custom-control custom-radio">
                              <input
                                type="radio"
                                id="sendImmediately"
                                name="deliveryDateOption"
                                class="custom-control-input"
                                value="immediate"
                                v-model="purchaseForm.delivery_date_option"
                                @change="onDeliveryDateOptionChange"
                              />
                              <label class="custom-control-label" for="sendImmediately">
                                Send Immediately
                              </label>
                            </div>
                            <div class="custom-control custom-radio">
                              <input
                                type="radio"
                                id="sendOnDate"
                                name="deliveryDateOption"
                                class="custom-control-input"
                                value="scheduled"
                                v-model="purchaseForm.delivery_date_option"
                                @change="onDeliveryDateOptionChange"
                              />
                              <label class="custom-control-label" for="sendOnDate">
                                Send on a specific date
                              </label>
                            </div>
                          </div>
                          <span v-if="giftcardFromErrors.delivery_date" class="text-danger small d-block mt-1">
                            {{ giftcardFromErrors.delivery_date }}
                          </span>
                        </div>
                      </div>

                      <!-- Calendar for Specific Date (only shown when "Send on a specific date" is selected) -->
                      <div class="col-md-6" v-if="purchaseForm.delivery_date_option === 'scheduled'">
                        <div class="form-group">
                          <label for="deliveryDate">Select Date <span class="text-danger">*</span></label>
                          <input
                            type="text"
                            id="deliveryDate"
                            class="form-control"
                            placeholder="Select Delivery Date"
                            :class="{ 'is-invalid': giftcardFromErrors.delivery_date }"
                            onkeydown="return false"
                            autocomplete="off"
                          />
                          <small class="form-text text-muted">Select a future date for delivery</small>
                        </div>
                      </div>
                    </template>

                    <!-- Info message for Print Code type -->
                    <div class="col-12" v-if="purchaseForm.delivery_type === 'printable'">
                      <div class="alert alert-danger">
                        <i class="fas fa-info-circle mr-2"></i>
                        <strong>Print Code for In-Person Gift:</strong> A unique Gift Card Code will be generated. No recipient details required. Make sure the Sender receives both the Gift Card Code and directions for the Recipient to go to www.canpaygift.com to access the gift card value with the code.
                      </div>
                    </div>
                  </div>

                  <!-- Message Section with Green Background -->
                  <div class="row">
                    <div class="col-12">
                      <div class="form-group">
                        <label for="message">Message:</label>
                        <!-- Message Selector Dropdown -->
                          <select
                            v-model="purchaseForm.selectedMessage"
                            class="form-control"
                            style="margin-bottom: 12px;"
                            @change="onMessageSelect"
                          >
                            <option value="">Select a message or write your own</option>
                            <option value="Happy Anniversary">Happy Anniversary</option>
                            <option value="Happy Birthday">Happy Birthday</option>
                            <option value="Merry Christmas">Merry Christmas</option>
                            <option value="Happy Holidays">Happy Holidays</option>
                            <option value="Happy Thanksgiving">Happy Thanksgiving</option>
                            <option value="Happy New Year">Happy New Year</option>
                            <option value="Congratulations">Congratulations</option>
                            <option value="Thank You">Thank You</option>
                          </select>
                        <!-- Message Textarea -->
                        <textarea
                          v-model="purchaseForm.message"
                          placeholder="Enjoy the gift card!"
                          class="form-control message-textarea"
                          rows="4"
                          maxlength="500"
                        ></textarea>
                        <small class="form-text text-muted">{{ purchaseForm.message ? purchaseForm.message.length : 0 }}/500 characters</small>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-12">
                      <button
                        type="submit"
                        class="btn btn-success"
                        :disabled="purchasing"
                      >
                        <i v-if="purchasing" class="fas fa-spinner fa-spin mr-2"></i>
                        <i v-else class="fas fa-gift mr-2"></i>
                        {{ purchasing ? 'Processing...' : 'Issue Gift Card to Recipient' }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-secondary ml-2"
                        @click="resetForm"
                      >
                        <i class="fas fa-undo mr-2"></i>
                        Reset Form
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            <!-- Accountant Read-Only Message -->
            <div class="alert alert-info" v-if="userDetails.role_name === 'Accountant'">
              <i class="fas fa-info-circle"></i>
              <strong>Read-Only Access:</strong> As an Accountant, you have access to view gift card purchase history and reports only. To issue gift cards, please contact your Corporate Parent or Regional Manager.
            </div>

            <!-- Purchase History Card -->
            <div class="card card-success">
              <div class="card-header">
                <h3 class="card-title">Recent Purchase History</h3>
                <b-button
                  class="btn-danger export-api-btn"
                  @click="reloadDatatable"
                  v-if="showReloadBtn"
                >
                  <i class="fas fa-redo"></i> Reload
                </b-button>
              </div>
              <div class="loading" v-if="loading">
                <hour-glass></hour-glass>
              </div>
              <div class="card-body table-responsive">
                <table
                  id="purchaseHistoryTable"
                  class="table table-striped table-bordered"
                  style="width: 100%; white-space: normal"
                >
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Store</th>
                      <th>Type</th>
                      <th>Reference Number</th>
                      <th>Sender's Name</th>
                      <th>Consumer</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Amount</th>
                      <th>Delivery Date</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody></tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>


    <!-- Message Display Modal -->
    <b-modal
      id="message-display-modal"
      ref="message-display-modal"
      title="Message"
      :header-text-variant="headerTextVariant"
      hide-footer
    >
      <!-- Body -->
      <div class="row odd-row">
        <div class="col-md-12 row-value">
          <p style="margin: 0; word-wrap: break-word; white-space: pre-wrap; line-height: 1.6;">
            {{ selectedMessageForDisplay }}
          </p>
        </div>
      </div>
    </b-modal>


    <!-- Confirmation Modal - Professional Design -->
    <b-modal
      id="gift-card-confirmation-modal"
      ref="gift-card-confirmation-modal"
      title="Confirm Gift Card Details"
      :header-text-variant="headerTextVariant"
      hide-footer
      modal-class="confirmation-modal-centered"
    >
      <div class="confirmation-modal-content">
        <!-- Delivery Type Badge -->
        <div class="text-center mb-3">
          <span v-if="purchaseForm.delivery_type === 'digital'" class="delivery-type-badge digital-badge">
            <i class="fas fa-envelope mr-2"></i>Email Gift Card
          </span>
          <span v-else class="delivery-type-badge coupon-badge">
            <i class="fas fa-ticket-alt mr-2"></i>Print Code for In-Person Gift
          </span>
        </div>

        <!-- Gift Card Value - Highlighted -->
        <div class="amount-highlight-box mb-3">
          <div class="amount-highlight-label">Gift Card Value</div>
          <div class="amount-highlight-value">${{ purchaseForm.amount }}</div>
        </div>

        <!-- Details Section -->
        <div class="details-section">
          <!-- Store -->
          <div class="detail-row">
            <div class="detail-label">
              <i class="fa fa-archive"></i>Store
            </div>
            <div class="detail-value">
              {{ selectedStore ? selectedStore.retailer : 'Not selected' }}
            </div>
          </div>

          <!-- Sender's Name (optional for coupon-based) -->
          <div v-if="purchaseForm.sender_name" class="detail-row">
            <div class="detail-label">
              <i class="fas fa-user"></i>Sender's Name
            </div>
            <div class="detail-value">
              {{ purchaseForm.sender_name }}
            </div>
          </div>

          <!-- Digital Gift Card Fields -->
          <template v-if="purchaseForm.delivery_type === 'digital'">
            <div class="detail-row">
              <div class="detail-label">
                <i class="fas fa-user"></i>Recipient's Name
              </div>
              <div class="detail-value">
                {{ purchaseForm.recipient_name }}
              </div>
            </div>

            <div class="detail-row">
              <div class="detail-label">
                <i class="fas fa-phone"></i>Recipient's Phone
              </div>
              <div class="detail-value">
                {{ purchaseForm.recipient_phone }}
              </div>
            </div>

            <div class="detail-row">
              <div class="detail-label">
                <i class="fas fa-envelope"></i>Recipient's Email
              </div>
              <div class="detail-value">
                {{ purchaseForm.recipient_email || 'Not provided' }}
              </div>
            </div>

            <div class="detail-row">
              <div class="detail-label">
                <i class="fas fa-calendar-alt"></i>Delivery Date
              </div>
              <div class="detail-value">
                {{ formatDisplayDate(purchaseForm.delivery_date) }}
              </div>
            </div>
          </template>

          <!-- Message (for both types) -->
          <div v-if="purchaseForm.message" class="detail-row">
            <div class="detail-label">
              <i class="fa fa-comment"></i>Message
            </div>
            <div class="detail-value message-value">
              {{ purchaseForm.message }}
            </div>
          </div>
        </div>

        <!-- Print Code Gift Card Info -->
        <div v-if="purchaseForm.delivery_type === 'printable'" class="info-box danger-info-box mt-3">
          <i class="fas fa-info-circle mr-2"></i>
          <span><strong>Note:</strong> A unique Gift Card Code will be generated after pressing Confirm & Issue. Write this code down for the Sender, and provide them with the website: www.canpaygift.com for the Gift Card Recipient to access the gift card funds.</span>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons mt-3">
          <button type="button" class="btn btn-secondary btn-action" @click="cancelPurchase">
            <i class="fas fa-times mr-2"></i>Cancel
          </button>
          <button type="button" class="btn btn-success btn-action" @click="confirmPurchase" :disabled="purchasing">
            <i v-if="purchasing" class="fas fa-spinner fa-spin mr-2"></i>
            <i v-else class="fas fa-check mr-2"></i>
            {{ purchasing ? 'Processing...' : 'Confirm & Issue' }}
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Print Code Confirmation Modal - Professional Design -->
    <b-modal
      id="print-code-confirmation-modal"
      ref="print-code-confirmation-modal"
      title="Print Code for in-person Gift"
      :header-text-variant="headerTextVariant"
      hide-footer
      modal-class="print-code-confirmation-centered"
    >
      <div class="print-code-confirmation-content">
        <!-- Warning Icon -->
        <div class="warning-icon-wrapper mb-3">
          <i class="fas fa-exclamation-triangle"></i>
        </div>

        <!-- Warning Messages -->
        <div class="warning-messages">
          <div class="warning-message-box mb-3">
            <p class="warning-text">
              <strong>When you choose this option, NO email or SMS will be sent to the recipient.</strong>
            </p>
            <p class="warning-text">
              You <strong>MUST</strong> write down the code for the Gift Card Sender and hand it to them.
            </p>
          </div>

          <div class="info-message-box mb-3">
            <p class="info-text">
              Once the Sender delivers the physical code to the Recipient, the Recipient will go to:
              <strong>www.canpaygift.com</strong> to access the gift card value with the code.
            </p>
          </div>

          <div class="alert-message-box mb-3">
            <i class="fas fa-shield-alt mr-2"></i>
            <strong>This Gift Card Code must be treated like cash.</strong>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons mt-3">
          <button type="button" class="btn btn-secondary btn-action" @click="cancelPrintCodeSelection">
            <i class="fas fa-times mr-2"></i>Cancel
          </button>
          <button type="button" class="btn btn-success btn-action" @click="confirmPrintCodeSelection">
            <i class="fas fa-check mr-2"></i>Confirm
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Coupon Code Success Modal for Print Code Gift Cards -->
    <b-modal
      id="coupon-code-modal"
      ref="coupon-code-modal"
      title="Gift Card Issued Successfully"
      :header-text-variant="headerTextVariant"
      hide-footer
      :no-close-on-esc="true"
      :no-close-on-backdrop="true"
      @close="closeCouponModal"
    >
      <div class="coupon-modal-content">
        <!-- Success Icon with Animation -->
        <div class="success-icon-wrapper mb-3">
          <i class="fas fa-check-circle text-success"></i>
        </div>

        <!-- Amount Display - Large and Prominent -->
        <div class="amount-display-wrapper mb-3">
          <div class="amount-label">Gift Card Value</div>
          <div class="amount-value" v-if="generatedGiftCardAmount">
            ${{ parseFloat(generatedGiftCardAmount).toFixed(2) }}
          </div>
          <div class="amount-value text-muted" v-else>N/A</div>
        </div>

        <!-- Reference Number Display -->
        <div class="reference-number-wrapper mb-3">
          <span class="reference-label">Reference Number:</span>
          <span class="reference-value" v-if="lastPurchasedGiftCardId">#{{ lastPurchasedGiftCardId }}</span>
          <span class="text-muted" v-else>N/A</span>
        </div>

        <!-- Coupon Code Display with Enhanced Styling -->
        <div class="coupon-code-wrapper mb-3">
          <div class="coupon-label mb-2">In-person Gift Card Code (write this down for the Sender)</div>
          <div class="coupon-code-display">
            <span v-if="generatedCouponCode">{{ generatedCouponCode }}</span>
            <span v-else class="text-muted">N/A</span>
          </div>
        </div>

        <!-- Copy Button -->
        <div class="copy-button-wrapper mb-3">
          <button
            type="button"
            class="btn btn-primary btn-copy"
            @click="copyCouponCode"
            :disabled="!generatedCouponCode"
          >
            <i class="fas fa-copy mr-2"></i>{{ copyButtonText }}
          </button>
          <button
            type="button"
            class="btn btn-secondary btn-copy ml-2"
            @click="printCouponCode"
            :disabled="!generatedCouponCode"
          >
            <i class="fas fa-print mr-2"></i>Print Code
          </button>
        </div>

        <!-- Redemption Instructions -->
        <div class="alert alert-info redemption-alert mb-2">
          <div class="alert-header">
            <i class="fas fa-mobile-alt mr-2"></i>
            <strong>How to Redeem:</strong>
          </div>
          <p class="alert-text mb-0">
            The Gift Card Recipient can go to <strong>www.canpaygift.com</strong> to access the gift card value with the Gift Card Code.
          </p>
        </div>

        <!-- Important Warning -->
        <div class="alert alert-warning warning-alert mb-3">
          <div class="alert-header">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>Important Note</strong>
          </div>
          <p class="alert-text mb-0">
            This Gift Card Code is shown <strong>only once</strong>. If you forget the code, you will need to contact <strong>Canpay Admin</strong> with the reference number <strong>#{{ lastPurchasedGiftCardId }}</strong>.
          </p>
        </div>

        <!-- Close Button -->
        <div class="close-button-wrapper">
          <button type="button" class="btn btn-success btn-close-modal" @click="closeCouponModal">
            <i class="fas fa-check mr-2"></i>I've written down the code. Close this window.
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import apiuser from "@/api/user.js";
import { HourGlass } from "vue-loading-spinner";
import Constants from "@/common/constant.js";
import { amountDecimalNumberFormatter } from "@/utils/formatters.js";
import moment from "moment";

export default {
  name: "MerchantGiftCard",
  data() {
    return {
      userDetails: JSON.parse(localStorage.getItem("store_user")),
      headerTextVariant: "light",
      constants: Constants,
      loading: false,
      showReloadBtn: false,
      purchasing: false,
      authorizedStores: [],
      selectedStore: null,
      purchaseForm: {
        store_id: '',
        delivery_type: 'digital', // Default to digital (Email)
        sender_name: '',
        recipient_name: '',
        recipient_phone: '',
        recipient_email: '',
        amount: '',
        message: 'Enjoy the gift card!',
        delivery_date: new Date().toISOString().split('T')[0],
        delivery_date_option: 'immediate', // Default to Send Immediately
        selectedMessage: ''
      },
      giftcardFromErrors: {},
      minDeliveryDate: new Date().toISOString().split('T')[0],
      selectedMessageForDisplay: '',
      generatedCouponCode: '', // Store generated coupon code for coupon-based cards
      merchantLogo: '', // Store merchant logo for printable card
      lastPurchasedGiftCardId: '', // Store last purchased gift card ID
      generatedGiftCardAmount: '', // Store gift card amount for display in modal
      copyButtonText: 'Copy Code' // Text for copy button
    };
  },
  components: {
    HourGlass,
  },
  mounted() {
    var self = this;
    self.loadAuthorizedStores();
    self.loadDT();
    document.title = "CanPay - Gift Card Purchase";

    // Initialize datepicker for delivery date
    $("#deliveryDate").datepicker({
      format: "mm/dd/yyyy",
      autoclose: true,
      todayHighlight: true,
      startDate: new Date()
    }).on('changeDate', function (e) {
      // Update the purchaseForm.delivery_date when date is selected
      self.purchaseForm.delivery_date = e.format('yyyy-mm-dd');
      // Clear any existing errors
      if (self.giftcardFromErrors.delivery_date) {
        delete self.giftcardFromErrors.delivery_date;
      }
    });

    // Set default date to today
    $("#deliveryDate").datepicker("setDate", new Date());
  },
  methods: {
    loadAuthorizedStores() {
      var self = this;
      self.loading = true;
      apiuser
        .getStores()
        .then((response) => {
          if (response.code == 200) {
            self.authorizedStores = response.data;
            // Auto-select if only one store exists
            if (self.authorizedStores.length === 1) {
              self.selectedStore = self.authorizedStores[0];
              self.purchaseForm.store_id = self.authorizedStores[0].id;
            }
            self.loading = false;
          } else {
            error(response.message);
            self.loading = false;
          }
        })
        .catch((err) => {
          error(err);
          self.loading = false;
        });
    },

    reloadDatatable() {
      var self = this;
      if ($.fn.DataTable.isDataTable("#purchaseHistoryTable")) {
        $("#purchaseHistoryTable").DataTable().ajax.reload();
      } else {
        self.loadDT();
      }
    },

    loadDT() {
      var self = this;

      // Check if table element exists
      if (!document.getElementById('purchaseHistoryTable')) {
        console.error('purchaseHistoryTable element not found');
        return;
      }

      // Destroy existing DataTable if it exists
      if ($.fn.DataTable.isDataTable("#purchaseHistoryTable")) {
        $("#purchaseHistoryTable").DataTable().destroy();
      }

      // Remove any existing event handlers to prevent duplicates
      $(document).off('click', '.show-more-link');

      // Add event handler for show more links
      $(document).on('click', '.show-more-link', function(e) {
        e.preventDefault();
        const message = $(this).data('message');
        self.showMessageModal(message);
      });

      $("#purchaseHistoryTable").DataTable({
        pagingType: "simple_numbers",
        processing: true,
        serverSide: true,
        destroy: true,
        columnDefs: [
          { orderable: false, targets: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
          { className: "dt-left", targets: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] }
        ],
        order: [[0, "desc"]],
        orderClasses: false,
        language: {
          processing:
            '<i class="fa fa-spinner fa-spin fa-3x fa-fw"></i><span class="sr-only">Loading...</span> ',
          emptyTable: "No Purchase History Available.",
          search: "_INPUT_",
          searchPlaceholder: "Search records",
          oPaginate: {
            sNext: '<i class="fas fa-angle-double-right"></i>',
            sPrevious: '<i class="fas fa-angle-double-left"></i>',
          },
          sLengthMenu:
            "<label class='label_dropdown_dt'>Per page</label> _MENU_",
        },
        ajax: {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
          url: process.env.VUE_APP_API_URL + "/merchant/giftcard/history",
          type: "GET",
          dataType: "json",
          data: function(d) {
            // Convert DataTable parameters to backend expected format
            d.draw = d.draw || 1;
            d.start = d.start || 0;
            d.length = d.length || 10;
            if (d.search && d.search.value) {
              d['search.value'] = d.search.value;
            }
            return d;
          },
          dataSrc: function (result) {
            return result.data;
          },
          error: function (request) {
            if (
              request.responseJSON &&
              request.responseJSON.code == 401 &&
              request.responseJSON.data != null
            ) {
              localStorage.setItem("token", request.responseJSON.data);
              self.loadDT();
            } else {
              error(Constants.datatable_error);
              $('#purchaseHistoryTable_processing').hide();
              self.showReloadBtn = true;
            }
          },
        },
        columns: [
          {
            render: function (data, type, full, meta) {
              return new Date(full.purchase_date).toLocaleDateString() + ' ' +
                     new Date(full.purchase_date).toLocaleTimeString();
            }
          },
          { data: "store_name" },
          {
            render: function (data, type, full, meta) {
              const deliveryType = full.delivery_type || 'digital';
              if (deliveryType === 'printable') {
                return '<span class="badge badge-info"><i class="fas fa-ticket-alt mr-1"></i>Print Code</span>';
              } else {
                return '<span class="badge badge-primary"><i class="fas fa-envelope mr-1"></i>Email</span>';
              }
            }
          },
          {
            render: function (data, type, full, meta) {
              return full.reference_number || 'N/A';
            }
          },
          { data: "sender_name" },
          {
            render: function (data, type, full, meta) {
              return full.recipient_name || '';
            }
          },
          {
            render: function (data, type, full, meta) {
              return full.recipient_phone || '';
            }
          },
          {
            render: function (data, type, full, meta) {
              return full.recipient_email || '';
            }
          },
          {
            render: function (data, type, full, meta) {
              return '$' +amountDecimalNumberFormatter(full.amount);
            }
          },
          {
            render: function (data, type, full, meta) {
              // Show redeemed date for coupon-based cards, delivery date for Email/SMS cards
              if (full.delivery_type === 'printable') {
                 if (full.redeemed_at) {
                  return new Date(full.redeemed_at).toLocaleDateString();
                } else {
                  return 'N/A';
                }
              }
              if (full.scheduled_sharing_date) {
                return new Date(full.scheduled_sharing_date).toLocaleDateString();
              }
              return 'N/A';
            }
          },
          {
            render: function (data, type, full, meta) {
              const message = full.message || '';
              if (message.length > 40) {
                const truncated = message.substring(0, 40) + '...';
                return truncated + ' <a href="#" class="show-more-link" data-message="' +
                       message.replace(/"/g, '&quot;') + '">show more</a>';
              }
              return message;
            }
          }
        ],
      });
    },

    validateForm() {
      // Clear all giftcardFromErrors before validation
      this.giftcardFromErrors = {};

      // Validate store selection
      if (!this.purchaseForm.store_id) {
        this.giftcardFromErrors.store_id = 'Please select a store';
      }

      // Validate delivery type selection
      if (!this.purchaseForm.delivery_type) {
        this.giftcardFromErrors.delivery_type = 'Please select a delivery type';
      }

      // Validate amount using dedicated method (required for both types)
      this.validateAmount();

      // For digital delivery type, validate all recipient fields
      if (this.purchaseForm.delivery_type === 'digital') {
        // Validate sender name using dedicated method (required for digital)
        this.validateSenderName();

        // Validate recipient name using dedicated method
        this.validateRecipientName();

        // Validate phone number
        if (!this.purchaseForm.recipient_phone) {
          this.giftcardFromErrors.recipient_phone = 'Recipient phone is required';
        } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(this.purchaseForm.recipient_phone)) {
          this.giftcardFromErrors.recipient_phone = 'Please enter a valid phone number in format: (555) 123-4567';
        }

        // Validate email (required field)
        this.validateEmail();

        // Validate delivery date
        this.validateDeliveryDate();
      } else if (this.purchaseForm.delivery_type === 'printable') {
        // For coupon-based type, sender name is optional, so only validate if provided
        if (this.purchaseForm.sender_name && this.purchaseForm.sender_name.trim()) {
          this.validateSenderName();
        }
      }

      // Return true if no giftcardFromErrors
      return Object.keys(this.giftcardFromErrors).length === 0;
    },
    purchaseGiftCard() {
      var self = this;
      if (!self.validateForm()) {
        error('Please fix the form Errors.');
        return;
      }

      // Show confirmation dialog
      self.showConfirmationModal();
    },
    showConfirmationModal() {
      this.$refs['gift-card-confirmation-modal'].show();
    },
    hideConfirmationModal() {
      this.$refs['gift-card-confirmation-modal'].hide();
    },
    confirmPurchase() {
      var self = this;
      self.purchasing = true;

      // Prepare form data based on delivery type
      let formDataToSend = {
        store_id: self.purchaseForm.store_id,
        delivery_type: self.purchaseForm.delivery_type,
        amount: self.purchaseForm.amount,
        message: self.purchaseForm.message
      };

      // Add sender name if provided (optional for coupon-based, required for digital)
      if (self.purchaseForm.sender_name) {
        formDataToSend.sender_name = self.purchaseForm.sender_name;
      }

      // For digital delivery, add recipient details and delivery date
      if (self.purchaseForm.delivery_type === 'digital') {
        // Determine delivery date based on option
        let deliveryDate;
        let isPreferredDate;

        if (self.purchaseForm.delivery_date_option === 'immediate') {
          // Send immediately - use today's date
          deliveryDate = new Date().toISOString().split('T')[0];
          isPreferredDate = 0;
        } else {
          // Send on specific date - get from datepicker
          const deliveryDateValue = $("#deliveryDate").val();
          deliveryDate = moment(deliveryDateValue, "MM/DD/YYYY").format("YYYY-MM-DD");

          // Calculate is_preferred_date: 1 if delivery date is greater than today, 0 otherwise
          const today = new Date().toISOString().split('T')[0];
          isPreferredDate = deliveryDate > today ? 1 : 0;
        }

        // Clean phone number - remove all non-digit characters before sending to API
        const cleanedPhoneNumber = self.purchaseForm.recipient_phone.replace(/\D/g, '');

        formDataToSend = {
          ...formDataToSend,
          recipient_name: self.purchaseForm.recipient_name,
          recipient_phone: cleanedPhoneNumber,
          recipient_email: self.purchaseForm.recipient_email,
          delivery_date: deliveryDate,
          is_preferred_date: isPreferredDate
        };
      }

      // Use axios directly with merchant token
      axios.defaults.headers.common["Authorization"] = localStorage.getItem("token");
      axios.post(process.env.VUE_APP_API_URL + '/merchant/giftcard/purchase', formDataToSend)
      .then(function (response) {
        const result = response.data;
        if (result.code == 200) {
          // For coupon-based cards, store the coupon code and transaction number
          if (self.purchaseForm.delivery_type === 'printable' && result.data) {
            self.generatedCouponCode = result.data.coupon_code;
            self.lastPurchasedGiftCardId = result.data.transaction_number;
            self.generatedGiftCardAmount = self.purchaseForm.amount;
            self.merchantLogo = result.data.merchant_logo;
            // Store amount before resetting form

            // Start - Custom: Modified as per user request
            // Reset form and reload datatable first
            // self.resetForm();
            self.reloadDatatable();

            // Show the coupon code modal after resetting
            self.showCouponCodeModal();
            // End - Custom: Modified as per user request
          } else {
            // Email/SMS gift card success message
            success('Gift card purchased successfully.');

            // Reset form and reload for Email/SMS cards
            self.resetForm();
            self.reloadDatatable();
          }
        } else {
          error(result.message || 'Failed to purchase gift card');
        }
        self.purchasing = false;
      })
      .catch((err) => {
        error('Failed to purchase gift card');
        self.purchasing = false;
      });
      self.hideConfirmationModal();
    },

    cancelPurchase() {
      this.hideConfirmationModal();
    },

    resetForm() {
      this.purchaseForm = {
        store_id: '',
        delivery_type: 'digital', // Reset to default (Email)
        sender_name: '',
        recipient_name: '',
        recipient_phone: '',
        recipient_email: '',
        amount: '',
        message: 'Enjoy the gift card!',
        delivery_date: new Date().toISOString().split('T')[0],
        delivery_date_option: 'immediate', // Reset to Send Immediately
        selectedMessage: ''
      };
      this.selectedStore = null;
      this.giftcardFromErrors = {};
      this.copyButtonText = 'Copy Code';

      // Reset datepicker to today's date
      $("#deliveryDate").datepicker("setDate", new Date());
    },

    onStoreSelect(store) {
      if (store) {
        this.purchaseForm.store_id = store.id;
        // Clear store error when selected
        if (this.giftcardFromErrors.store_id) {
          delete this.giftcardFromErrors.store_id;
        }
      }
    },

    onMessageSelect() {
      // When a predefined message is selected, populate the textarea
      // The value is already bound via v-model, so just copy it to message
      if (this.purchaseForm.selectedMessage) {
        this.purchaseForm.message = this.purchaseForm.selectedMessage;
      }
    },

    onDeliveryTypeChange() {
      var self = this;
      // Clear delivery type error when a type is selected
      if (this.purchaseForm.delivery_type) {
        delete this.giftcardFromErrors.delivery_type;
      }

      // Show confirmation popup for Print Code option
      if (this.purchaseForm.delivery_type === 'printable') {
        // Show the custom professional modal
        this.$refs['print-code-confirmation-modal'].show();
      } else if (this.purchaseForm.delivery_type === 'digital') {
        // Re-initialize datepicker when switching to digital type
        this.$nextTick(() => {
          if ($("#deliveryDate").length && !$("#deliveryDate").data('datepicker')) {
            $("#deliveryDate").datepicker({
              format: "mm/dd/yyyy",
              autoclose: true,
              todayHighlight: true,
              startDate: new Date()
            }).on('changeDate', function (e) {
              self.purchaseForm.delivery_date = e.format('yyyy-mm-dd');
              if (self.giftcardFromErrors.delivery_date) {
                delete self.giftcardFromErrors.delivery_date;
              }
            });
            $("#deliveryDate").datepicker("setDate", new Date());
          }
        });
      }
    },

    confirmPrintCodeSelection() {
      // User confirmed, clear recipient fields
      delete this.giftcardFromErrors.recipient_name;
      delete this.giftcardFromErrors.recipient_phone;
      delete this.giftcardFromErrors.recipient_email;
      delete this.giftcardFromErrors.delivery_date;

      this.purchaseForm.recipient_name = '';
      this.purchaseForm.recipient_phone = '';
      this.purchaseForm.recipient_email = '';

      // Close the modal
      this.$refs['print-code-confirmation-modal'].hide();
    },

    cancelPrintCodeSelection() {
      // User clicked Cancel, revert to digital
      this.purchaseForm.delivery_type = 'digital';

      // Close the modal
      this.$refs['print-code-confirmation-modal'].hide();
    },

    onDeliveryDateOptionChange() {
      var self = this;
      // Clear delivery date error when option changes
      if (this.giftcardFromErrors.delivery_date) {
        delete this.giftcardFromErrors.delivery_date;
      }

      if (this.purchaseForm.delivery_date_option === 'immediate') {
        // Set delivery date to today
        this.purchaseForm.delivery_date = new Date().toISOString().split('T')[0];
      } else if (this.purchaseForm.delivery_date_option === 'scheduled') {
        // Initialize datepicker for scheduled delivery
        this.$nextTick(() => {
          if ($("#deliveryDate").length) {
            // Destroy existing datepicker if it exists
            if ($("#deliveryDate").data('datepicker')) {
              $("#deliveryDate").datepicker('destroy');
            }

            // Initialize new datepicker
            $("#deliveryDate").datepicker({
              format: "mm/dd/yyyy",
              autoclose: true,
              todayHighlight: true,
              startDate: new Date()
            }).on('changeDate', function (e) {
              self.purchaseForm.delivery_date = e.format('yyyy-mm-dd');
              if (self.giftcardFromErrors.delivery_date) {
                delete self.giftcardFromErrors.delivery_date;
              }
            });
            $("#deliveryDate").datepicker("setDate", new Date());
          }
        });
      }
    },

    showPrintCodeInfo() {
      this.$bvModal.msgBoxOk(
        'Print Code for in-person Gift: make sure the Sender receives both the Gift Card Code and directions for the Recipient to go to www.canpaygift.com to access the gift card value with the code.',
        {
          title: 'Information',
          size: 'md',
          buttonSize: 'md',
          okVariant: 'primary',
          okTitle: 'OK',
          headerClass: 'p-2 border-bottom-0',
          footerClass: 'p-2 border-top-0',
          centered: true
        }
      );
    },

    formatPhoneNumber(event) {
      let value = event.target.value.replace(/\D/g, ''); // Remove all non-digits

      if (value.length > 10) {
        value = value.substring(0, 10);
      }

      let formattedValue = '';
      if (value.length > 0) {
        formattedValue = '(' + value.substring(0, 3);
        if (value.length >= 4) {
          formattedValue += ') ' + value.substring(3, 6);
          if (value.length >= 7) {
            formattedValue += '-' + value.substring(6, 10);
          }
        } else if (value.length > 3) {
          formattedValue += ')';
        }
      }

      this.purchaseForm.recipient_phone = formattedValue;

      // Clear phone error while typing
      if (this.giftcardFromErrors.recipient_phone && value.length > 0) {
        delete this.giftcardFromErrors.recipient_phone;
      }
    },

    validatePhone() {
      const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
      if (this.purchaseForm.recipient_phone && !phoneRegex.test(this.purchaseForm.recipient_phone)) {
        this.giftcardFromErrors.recipient_phone = 'Please enter a valid phone number in format: (555) 123-4567';
      } else if (this.purchaseForm.recipient_phone) {
        delete this.giftcardFromErrors.recipient_phone;
      }
    },

    // ✅ Professional Email Validation - Real-time on Input
    validateEmailOnInput() {
      const value = this.purchaseForm.recipient_email;

      // Clear error while typing if user is correcting
      if (value && value.trim().length >= 5 && this.giftcardFromErrors.recipient_email) {
        delete this.giftcardFromErrors.recipient_email;
      }
    },

    // ✅ Professional Email Validation - On Blur (Required Field)
    validateEmail() {
      const value = this.purchaseForm.recipient_email;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!value || value.trim() === '') {
        this.giftcardFromErrors.recipient_email = 'Consumer email is required';
        return false;
      }

      if (!emailRegex.test(value.trim())) {
        this.giftcardFromErrors.recipient_email = 'Please enter a valid email address';
        return false;
      }

      // Clear error if validation passes
      delete this.giftcardFromErrors.recipient_email;
      return true;
    },

    // ✅ Professional Amount Validation - Real-time on Input
    validateAmountOnInput(event) {
      let value = event.target.value;

      // Remove any non-numeric characters except decimal point
      value = value.replace(/[^\d.]/g, '');

      // Ensure only one decimal point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2);
      }

      // Update the model with cleaned value
      this.purchaseForm.amount = value;

      // Clear error while typing if value is being corrected
      if (value && this.giftcardFromErrors.amount) {
        delete this.giftcardFromErrors.amount;
      }
    },

    // ✅ Professional Amount Validation - On Blur
    validateAmount() {
      const value = this.purchaseForm.amount;

      // Pattern: allows numbers with up to 2 decimal places
      const amountPattern = /^\d+(\.\d{1,2})?$/;

      if (!value || value.trim() === '') {
        this.giftcardFromErrors.amount = 'Amount is required';
        return false;
      }

      if (!amountPattern.test(value)) {
        this.giftcardFromErrors.amount = 'Amount must be a valid number with up to 2 decimal places';
        return false;
      }

      const numValue = parseFloat(value);

      if (isNaN(numValue)) {
        this.giftcardFromErrors.amount = 'Please enter a valid amount';
        return false;
      }

      if (numValue <= 0) {
        this.giftcardFromErrors.amount = 'Minimum amount is $0.01';
        return false;
      }

      // if (numValue > 1000) {
      //   this.giftcardFromErrors.amount = 'Maximum amount is $1000.00';
      //   return false;
      // }

      // Format to 2 decimal places
      this.purchaseForm.amount = numValue.toFixed(2);

      // Clear error if validation passes
      delete this.giftcardFromErrors.amount;
      return true;
    },

    // ✅ Professional Sender Name Validation - Real-time on Input
    validateSenderNameOnInput() {
      const value = this.purchaseForm.sender_name;

      // Clear error while typing if user is correcting
      if (value && value.trim().length >= 2 && this.giftcardFromErrors.sender_name) {
        delete this.giftcardFromErrors.sender_name;
      }
    },

    // ✅ Professional Sender Name Validation - On Blur
    validateSenderName() {
      const value = this.purchaseForm.sender_name;

      if (!value || value.trim() === '') {
        this.giftcardFromErrors.sender_name = 'Sender name is required';
        return false;
      }

      if (value.trim().length < 2) {
        this.giftcardFromErrors.sender_name = 'Sender name must be at least 2 characters';
        return false;
      }

      // Clear error if validation passes
      delete this.giftcardFromErrors.sender_name;
      return true;
    },

    // ✅ Professional Recipient Name Validation - Real-time on Input
    validateRecipientNameOnInput() {
      const value = this.purchaseForm.recipient_name;

      // Clear error while typing if user is correcting
      if (value && value.trim().length >= 2 && this.giftcardFromErrors.recipient_name) {
        delete this.giftcardFromErrors.recipient_name;
      }
    },

    // ✅ Professional Recipient Name Validation - On Blur
    validateRecipientName() {
      const value = this.purchaseForm.recipient_name;

      if (!value || value.trim() === '') {
        this.giftcardFromErrors.recipient_name = 'Recipient name is required';
        return false;
      }

      if (value.trim().length < 2) {
        this.giftcardFromErrors.recipient_name = 'Recipient name must be at least 2 characters';
        return false;
      }

      if (value.trim().length > 100) {
        this.giftcardFromErrors.recipient_name = 'Consumer name cannot exceed 100 characters';
        return false;
      }

      // Check for valid characters (letters, spaces, hyphens, apostrophes)
      const namePattern = /^[a-zA-Z\s'-]+$/;
      if (!namePattern.test(value.trim())) {
        this.giftcardFromErrors.recipient_name = 'Consumer name can only contain letters, spaces, hyphens, and apostrophes';
        return false;
      }

      // Clear error if validation passes
      delete this.giftcardFromErrors.recipient_name;
      return true;
    },

    // ✅ Delivery Date Validation (works with Bootstrap Datepicker)
    validateDeliveryDate() {
      // If Send Immediately is selected, no validation needed
      if (this.purchaseForm.delivery_date_option === 'immediate') {
        delete this.giftcardFromErrors.delivery_date;
        return true;
      }

      // For scheduled delivery, validate the date
      const selectedDate = this.purchaseForm.delivery_date;

      if (!selectedDate) {
        this.giftcardFromErrors.delivery_date = 'Delivery date is required';
        return false;
      }

      const today = new Date().toISOString().split('T')[0];

      if (selectedDate < today) {
        this.giftcardFromErrors.delivery_date = 'Delivery date cannot be in the past';
        return false;
      }

      // Clear error if validation passes
      delete this.giftcardFromErrors.delivery_date;
      return true;
    },

    // ✅ Show Message Modal
    showMessageModal(message) {
      this.selectedMessageForDisplay = message;
      this.$refs['message-display-modal'].show();
    },

    // ✅ Hide Message Modal
    hideMessageModal() {
      this.$refs['message-display-modal'].hide();
      this.selectedMessageForDisplay = '';
    },

    // ✅ Format amount with 2 decimal places
    amountDecimalNumberFormatter(val) {
      return amountDecimalNumberFormatter(val);
    },

    // ✅ Format date for display in confirmation modal
    formatDisplayDate(dateString) {
      if (!dateString) return 'Not set';
      const date = new Date(dateString + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    // ✅ Show Coupon Code Modal
    showCouponCodeModal() {
      this.copyButtonText = 'Copy Code'; // Reset button text
      this.$refs['coupon-code-modal'].show();
    },

    // ✅ Close Coupon Code Modal
    closeCouponModal() {
      this.$refs['coupon-code-modal'].hide();
      // Reset form on modal close as per request
      this.resetForm();
      
      this.generatedCouponCode = null;
      this.merchantLogo = null;
      this.lastPurchasedGiftCardId = null;
      this.generatedGiftCardAmount = null;
      this.copyButtonText = 'Copy Code';
    },

    // ✅ Print Coupon Code
    printCouponCode() {
      if (!this.generatedCouponCode) return;

      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) return;
      
      const styles = `
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap');
        
        body { 
          font-family: 'Roboto', 'Helvetica Neue', Helvetica, Arial, sans-serif; 
          background-color: #f4f6f9; 
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100vh;
        }
        
        .card-container {
          background-color: white;
          width: 500px;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
          font-family: 'Roboto', sans-serif;
        }
        
        .header {
          background-color: #1e8e3e; /* CanPay Green */
          color: white;
          padding: 25px 20px;
          text-align: center;
        }
        
        .code-text {
          font-family: 'Courier New', Courier, monospace;
          font-weight: bold;
          font-size: 24px;
          letter-spacing: 2px;
          text-transform: uppercase;
        }
        
        .body {
          padding: 30px;
          display: flex;
          justify-content: space-between;
        }
        
        .left-section {
          display: flex;
          flex-direction: column;
          gap: 25px;
        }
        
        .store-info {
          display: flex;
          align-items: flex-start;
          gap: 15px;
        }
        
        .store-logo {
          width: 45px;
          height: 45px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #8cc63f; /* Light green for logo icon */
        }
        
        .store-logo i {
            font-size: 40px;
        }

        .store-logo img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
        
        .store-text {
          display: flex;
          flex-direction: column;
        }
        
        .store-name {
          color: #0056b3; /* Dark Blue/Teal similar to screenshot */
          font-size: 18px;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 4px;
        }
        
        .store-subtitle {
          font-size: 11px;
          color: #6c757d;
          line-height: 1.3;
        }
        
        .amount-box {
          margin-top: 5px;
        }
        
        .amount-label {
          font-size: 11px;
          color: #adb5bd;
          margin-bottom: 2px;
          font-weight: 500;
        }
        
        .amount-value {
          font-size: 32px;
          font-weight: 900;
          color: #212529;
          line-height: 1;
        }
        
        .right-section {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          text-align: right;
          padding-left: 20px;
        }
        
        .message {
          font-size: 14px;
          color: #495057;
          line-height: 1.4;
          max-width: 180px;
          word-wrap: break-word;
        }
        
        .sender-info {
          font-size: 12px;
          color: #6c757d;
          margin-top: 20px;
        }
        
        .sender-name {
          font-weight: 700;
          color: #212529;
        }

        @media print {
            body { 
                background: white; 
                height: auto;
                display: block;
            }
            .card-container {
                box-shadow: none;
                border: 1px solid #dee2e6;
                margin: 20px auto;
            }
            .header {
               -webkit-print-color-adjust: exact; 
               print-color-adjust: exact;
            }
        }

        .footer {
          text-align: center;
          padding: 15px 20px;
          background: #f8fbf9;
          border-top: 1px solid #eee;
          font-size: 14px;
          color: #555;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .footer i {
          color: #1e8e3e;
          font-size: 16px;
        }

        .footer strong {
          color: #1e8e3e;
          font-weight: 800;
          letter-spacing: 0.3px;
        }
      `;

      // Helper to format amount
      const amount = parseFloat(this.generatedGiftCardAmount).toFixed(2);
      
      const senderHtml = this.purchaseForm.sender_name 
        ? `<div class="sender-info">Gifted by: <span class="sender-name">${this.purchaseForm.sender_name}</span></div>` 
        : '';

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Gift Card Print</title>
            <meta charset="utf-8">
            <style>${styles}</style>
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css">
          </head>
          <body>
            <div class="card-container">
              <div class="header">
                <div class="code-text">${this.generatedCouponCode}</div>
              </div>
              <div class="body">
                <div class="left-section">
                  <div class="store-info">
                    <div class="store-logo">
                    <img src="${this.merchantLogo ? this.merchantLogo : window.location.origin + '/img/Giftcard-logo-new-white.png'}" />
                </div>
                    <div class="store-text">
                      <div class="store-name">${this.selectedStore ? this.selectedStore.retailer : 'Store Name'}</div>
                      <div class="store-subtitle">Canpay — Merchant<br>Gift Card</div>
                    </div>
                  </div>
                  
                  <div class="amount-box">
                    <div class="amount-label">Amount</div>
                    <div class="amount-value">$${amount}</div>
                  </div>
                </div>
                
                <div class="right-section">
                  <div class="message">
                    ${this.purchaseForm.message || ''}
                  </div>
                  ${senderHtml}
                </div>
              </div>
              <div class="footer">
                <i class="fas fa-globe"></i>
                <span>Visit <strong>www.canpaygift.com</strong> to activate</span>
              </div>
            </div>
            
            <script>
              (function() {
                var hasPrinted = false;
                var closeTimer = null;

                function closePrintWindow() {
                  if (closeTimer) {
                    clearTimeout(closeTimer);
                  }
                  closeTimer = setTimeout(function() {
                    window.close();
                  }, 250);
                }

                function handlePrintStateChange(event) {
                  if (!event.matches && hasPrinted) {
                    closePrintWindow();
                  }
                }

                window.addEventListener('afterprint', closePrintWindow);

                // Safari fallback: focus typically returns after the print dialog closes.
                window.addEventListener('focus', function() {
                  if (hasPrinted) {
                    closePrintWindow();
                  }
                });

                var mediaQueryList = window.matchMedia ? window.matchMedia('print') : null;
                if (mediaQueryList) {
                  if (typeof mediaQueryList.addEventListener === 'function') {
                    mediaQueryList.addEventListener('change', handlePrintStateChange);
                  } else if (typeof mediaQueryList.addListener === 'function') {
                    mediaQueryList.addListener(handlePrintStateChange);
                  }
                }

                window.onload = function() {
                  setTimeout(function() {
                    hasPrinted = true;
                    window.print();

                    // Extra Safari guard where neither afterprint nor media query change fire.
                    closeTimer = setTimeout(closePrintWindow, 1500);
                  }, 700);
                };
              })();
            <\/script>
          </body>
        </html>
      `;
      
      printWindow.document.write(html);
      printWindow.document.close();
    },

    // ✅ Copy Coupon Code to Clipboard
    copyCouponCode() {
      if (this.generatedCouponCode) {
        const fullCouponCode = this.generatedCouponCode;

        // Use the Clipboard API
        navigator.clipboard.writeText(fullCouponCode)
          .then(() => {
            // Update button text to show success
            this.copyButtonText = 'Copied!';
            success('Coupon code copied to clipboard!');

            // Reset button text after 2 seconds
            setTimeout(() => {
              this.copyButtonText = 'Copy Code';
            }, 2000);
          })
          .catch(() => {
            error('Failed to copy coupon code');
          });
      }
    }
  }
};
</script>

<style scoped>
.input-group-text {
  background-color: #f8f9fa;
  border-color: #ced4da;
}

.text-danger {
  color: #dc3545 !important;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}

.loading {
  text-align: center;
  padding: 20px;
}

/* ===== Professional Confirmation Modal Styling - Compact ===== */
/* Center the modal */
.confirmation-modal-centered .modal-dialog {
  display: flex;
  align-items: center;
  min-height: calc(100% - 1rem);
}

.confirmation-modal-content {
  padding: 5px;
  text-align: center;
}

/* Delivery Type Badge - Compact */
.delivery-type-badge {
  display: inline-block;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 50px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.digital-badge {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: #ffffff;
}

.coupon-badge {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: #ffffff;
}

/* Amount Highlight Box - Compact */
.amount-highlight-box {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #28a745;
  border-radius: 10px;
  padding: 12px 15px;
  text-align: center;
  box-shadow: 0 3px 10px rgba(40, 167, 69, 0.2);
  margin: 0 auto;
  max-width: 300px;
}

.amount-highlight-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 5px;
}

.amount-highlight-value {
  font-size: 2rem;
  font-weight: bold;
  color: #28a745;
  line-height: 1;
}

/* Details Section - Compact */
.details-section {
  background: #ffffff;
  border-radius: 8px;
  padding: 0;
  text-align: left;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 12px;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row:hover {
  background-color: #f8f9fa;
}

.detail-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
  min-width: 150px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.detail-label i {
  width: 18px;
  min-width: 18px;
  text-align: center;
  font-size: 0.9rem;
  color: #007bff !important;
  flex-shrink: 0;
  display: inline-block;
  margin-right: 0;
}

.detail-value {
  color: #212529;
  font-size: 0.85rem;
  text-align: right;
  flex: 1;
  word-break: break-word;
}

.message-value {
  font-style: italic;
  color: #6c757d;
  max-width: 250px;
  line-height: 1.4;
}

/* Info Box - Compact */
.info-box {
  background: linear-gradient(135deg, #e7f3ff 0%, #d4edff 100%);
  border-left: 3px solid #007bff;
  padding: 8px 12px;
  border-radius: 5px;
  font-size: 0.8rem;
  color: #004085;
  line-height: 1.5;
  text-align: left;
}

.danger-info-box {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe3e3 100%);
  border-left: 3px solid #dc3545;
  color: #c92a2a;
}

/* Action Buttons - Compact */
.action-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  padding-top: 8px;
  border-top: 2px solid #e9ecef;
}

.btn-action {
  min-width: 120px;
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 5px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
}

.btn-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .detail-row {
    flex-direction: column;
    gap: 6px;
    padding: 8px 10px;
  }

  .detail-label {
    min-width: auto;
    font-size: 0.8rem;
  }

  .detail-value {
    text-align: left;
    font-size: 0.8rem;
  }

  .amount-highlight-value {
    font-size: 1.75rem;
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn-action {
    width: 100%;
  }
}

/* Badge styling for confirmation modal */
.badge-lg {
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
}

/* Alert styling */
.alert-info {
  background-color: #e7f3ff;
  border-color: #b3d9ff;
  color: #004085;
}

/* ===== Enhanced Coupon Modal Styling - Compact Version ===== */
.coupon-modal-content {
  text-align: center;
  padding: 5px 5px;
}

/* Success Icon */
.success-icon-wrapper {
  animation: scaleIn 0.5s ease-out;
  margin-bottom: 8px;
}

.success-icon-wrapper i {
  font-size: 36px;
  color: #28a745;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Amount Display - Compact */
.amount-display-wrapper {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  padding: 12px 12px;
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(40, 167, 69, 0.25);
  margin: 0 auto 10px;
  max-width: 300px;
}

.amount-label {
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 4px;
}

.amount-value {
  font-size: 28px;
  font-weight: bold;
  color: #ffffff;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Reference Number - Compact */
.reference-number-wrapper {
  font-size: 13px;
  color: #495057;
  margin-bottom: 10px;
}

.reference-label {
  font-weight: 600;
  color: #6c757d;
  margin-right: 4px;
}

.reference-value {
  font-weight: bold;
  color: #17a2b8;
  font-size: 14px;
}

/* Coupon Code Display - Compact */
.coupon-code-wrapper {
  margin: 0 auto 10px;
  max-width: 400px;
}

.coupon-label {
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}

.coupon-code-display {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 10px 15px;
  border-radius: 6px;
  border: 2px dashed #6c757d;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Courier New', monospace;
  color: #212529;
  letter-spacing: 1.2px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.coupon-code-display:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
}

/* Copy Button - Compact */
.copy-button-wrapper {
  margin: 0 auto 10px;
}

.btn-copy {
  min-width: 140px;
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 5px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 123, 255, 0.25);
  font-size: 14px;
}

.btn-copy:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0, 123, 255, 0.35);
}

.btn-copy:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Alert Boxes - Compact */
.redemption-alert,
.warning-alert {
  text-align: left;
  border-radius: 5px;
  padding: 8px 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 8px;
}

.redemption-alert {
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  color: #004085;
}

.warning-alert {
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  color: #856404;
}

.alert-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  font-size: 12px;
}

.alert-header i {
  font-size: 14px;
}

.alert-text {
  font-size: 11px;
  line-height: 1.4;
}

/* Close Button - Compact */
.close-button-wrapper {
  text-align: center;
  margin-top: 8px;
}

.btn-close-modal {
  min-width: 110px;
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 5px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(40, 167, 69, 0.25);
  font-size: 14px;
}

.btn-close-modal:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(40, 167, 69, 0.35);
}

/* Responsive adjustments - Compact */
@media (max-width: 576px) {
  .amount-value {
    font-size: 24px;
  }

  .coupon-code-display {
    font-size: 16px;
    padding: 8px 12px;
    letter-spacing: 0.8px;
  }

  .amount-display-wrapper {
    padding: 10px 10px;
  }

  .success-icon-wrapper i {
    font-size: 32px;
  }

  .alert-text {
    font-size: 10px;
  }

  .alert-header {
    font-size: 11px;
  }
}

/* ===== Print Code Confirmation Modal Styling ===== */
.print-code-confirmation-centered .modal-dialog {
  display: flex;
  align-items: center;
  min-height: calc(100% - 1rem);
}

.print-code-confirmation-content {
  padding: 10px;
  text-align: center;
}

/* Warning Icon */
.warning-icon-wrapper {
  animation: pulseWarning 1.5s ease-in-out infinite;
  margin-bottom: 15px;
}

.warning-icon-wrapper i {
  font-size: 48px;
  color: #ffc107;
  filter: drop-shadow(0 4px 8px rgba(255, 193, 7, 0.3));
}

@keyframes pulseWarning {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

/* Warning Messages */
.warning-messages {
  text-align: left;
  margin: 0 auto;
  max-width: 500px;
}

.warning-message-box {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-left: 4px solid #ffc107;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
}

.warning-text {
  color: #856404;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 8px;
}

.warning-text:last-child {
  margin-bottom: 0;
}

.info-message-box {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
  border-left: 4px solid #17a2b8;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(23, 162, 184, 0.15);
}

.info-text {
  color: #0c5460;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 0;
}

.alert-message-box {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  border-left: 4px solid #dc3545;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.15);
  color: #721c24;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert-message-box i {
  font-size: 18px;
  margin-right: 8px;
}

/* Responsive adjustments for Print Code modal */
@media (max-width: 576px) {
  .warning-icon-wrapper i {
    font-size: 40px;
  }

  .warning-text,
  .info-text {
    font-size: 13px;
  }

  .alert-message-box {
    font-size: 13px;
  }

  .warning-message-box,
  .info-message-box,
  .alert-message-box {
    padding: 12px;
  }
}

</style>
